// src/components/questionnaire/DynamicQuestionnaireForm.jsx
/* ------------------------------------------------------------------ */
/* DynamicQuestionnaireForm – runtime-driven multi-step assessment     */
/* ------------------------------------------------------------------ */
/**
 * Flow
 * ----
 * 1. **Load definition** – pulls an array of questions from
 *    `utils/questionnaire/questions/{name}.js` via `loadQuestionnaire()`.
 * 2. **Filter** – drops questions whose `condition(formData[, user])` returns
 *    `false` (`filterQuestions()`).
 * 3. **Build steps** – `buildSteps()` converts the flat list into renderable
 *    *steps*:
 *        • `single`  – one question<br/>
 *        • `group`   – consecutive questions sharing the same `group` key<br/>
 *        • `review`  – read-only summary before submission (auto-appended)
 * 4. **Prefill** – `prefillRangeDefaults()` seeds untouched `range` controls
 *    with their `min` value so validation doesn’t block the user.
 * 5. **Submit** – on the final step the user’s answers are uploaded to Firebase
 *    Storage as JSON at:<br/>
 *    `questionnaires/{uid}/{name}/{ISO-timestamp}-{5rnd}.json`
 *
 * @component
 * @returns {JSX.Element}
 */

import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getStorage } from "firebase/storage";
import LinearProgress from "@mui/material/LinearProgress";

import { useAuth } from "../../context/AuthContext";
import {
  loadQuestionnaire,
  filterQuestions,
  buildSteps,
  prefillRangeDefaults,
} from "../../utils/questionnaireQuestions/questionnaireRuntime";

import { isQuestionValid } from "../../utils/form/isQuestionValid";
import { renderStep } from "../../utils/questionnaireQuestions/renderStep";
import { uploadResponses } from "../../utils/questionnaireQuestions/uploadResponses";

/* ------------------------------------------------------------------ */
/* React component                                                    */
/* ------------------------------------------------------------------ */
function DynamicQuestionnaireForm({ selectedPatient }) {
  const { user } = useAuth();
  const { questionnaireName } = useParams();
  const navigate = useNavigate();

  /* ---------------- State ------------------------------------------ */
  const [questions, setQuestions] = useState([]);
  const [formData, setFormData] = useState({});
  const [currentStep, setCurrentStep] = useState(0);

  /* ---------------- Load questionnaire ----------------------------- */
  useEffect(() => {
    loadQuestionnaire(questionnaireName)
      .then(setQuestions)
      .catch((err) => {
        console.error("Failed to load questionnaire:", err);
        setQuestions([]);
      });
  }, [questionnaireName]);

  /* ---------------- Derived values -------------------------------- */
  const filteredQuestions = useMemo(
    () => filterQuestions(questions, formData, user),
    [questions, formData, user]
  );

  const steps = useMemo(
    () => buildSteps(filteredQuestions),
    [filteredQuestions]
  );
  const totalSteps = steps.length;
  const progress = (currentStep / totalSteps) * 100;

  /* ---------------- Prefill range defaults ------------------------ */
  useEffect(() => {
    const defaults = prefillRangeDefaults(filteredQuestions, formData);
    if (Object.keys(defaults).length) {
      setFormData((prev) => ({ ...prev, ...defaults }));
    }
  }, [filteredQuestions]);

  /* ---------------- Handlers -------------------------------------- */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const goPrev = (e) => {
    e.preventDefault();
    setCurrentStep((s) => Math.max(0, s - 1));
  };

  const goNext = async (e) => {
    e?.preventDefault();

    /* ---------- Final step → upload & redirect -------------------- */
    if (currentStep === totalSteps - 1) {
      console.log(selectedPatient);
      const uid = selectedPatient ? selectedPatient.uid : user.uid;
      console.log(uid);
      try {
        await uploadResponses(
          questionnaireName,
          formData,
          uid,
          user.uid,
          getStorage()
        );
        navigate("/questionnaires");
      } catch (err) {
        console.error("Upload failed:", err);
        alert("Failed to save questionnaire. Please try again.");
      }
      return;
    }

    /* ---------- Validate current step ----------------------------- */
    const step = steps[currentStep];
    const invalid =
      step.type === "single"
        ? !isQuestionValid(step.question, formData)
        : step.questions.some((q) => !isQuestionValid(q, formData));

    if (invalid) {
      alert("Please complete all required fields to proceed.");
      return;
    }

    setCurrentStep((s) => s + 1);
  };

  /* ---------------- Render ---------------------------------------- */
  return (
    <div className="user-form p-4 w-full h-fit flex flex-col gap-8">
      <LinearProgress
        variant="determinate"
        value={progress}
        className="rounded-full"
        sx={{
          backgroundColor: "#B776B9",
          "& .MuiLinearProgress-bar": { backgroundColor: "#852170" },
        }}
      />

      {renderStep(
        steps,
        currentStep,
        formData,
        handleChange,
        goNext,
        filteredQuestions
      )}

      <div className="flex justify-between">
        {currentStep > 0 ? (
          <button onClick={goPrev} className="outlined-button">
            Previous
          </button>
        ) : (
          <div />
        )}
        <button onClick={goNext} className="primary-button">
          {currentStep === totalSteps - 1 ? "Confirm" : "Next"}
        </button>
      </div>
    </div>
  );
}

export default DynamicQuestionnaireForm;
